import { TestTarget } from "../types/TestTarget";

/**
 * 機能（feature）判定。feature は SSTF 上 type-less + mainProcedure のターゲットで、
 * 型変換時に type が "item" へフォールバックされるため、type では項目と判別できない。
 * 判定自体は変換時（procedureClient.isFeatureTarget）に確定した isFeature を参照する。
 */
export function is_feature_target(t: TestTarget): boolean {
  return t.isFeature === true;
}

/** View.params から component_ids を読み取る */
export function get_component_ids_from_view_params(params: unknown): string[] {
  if (!params || typeof params !== "object") {
    return [];
  }
  const raw = (params as { component_ids?: unknown }).component_ids;
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map((x) => String(x));
}

/** 先頭からの順序を保ち、trim 済みの重複 ID を除去する */
export function unique_trimmed_component_ids(ids: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of ids) {
    const id = raw.trim();
    if (id.length === 0) {
      continue;
    }
    if (seen.has(id)) {
      continue;
    }
    seen.add(id);
    out.push(id);
  }
  return out;
}

export function resolve_component_by_stored_id(
  storedId: string,
  targets: TestTarget[]
): TestTarget | undefined {
  const id = storedId.trim();
  if (!id) {
    return undefined;
  }
  return targets.find(
    (t) =>
      t.type === "component" &&
      (t.id === id || (t.test_target_id != null && t.test_target_id === id))
  );
}

/**
 * 指定ルート配下の DFS で component-ref ノードの referenceTargetId を収集する。
 *
 * - type==="component-ref" のノードに到達したら referenceTargetId を収集し、子には降りない（リーフ扱い）。
 * - type==="component" または type==="view" のノードには降りない（境界）。
 * - それ以外（section / overlay / item 等）には再帰降下する。
 * - dedup は first-seen 順（unique_trimmed_component_ids と同じ方針）。
 */
export function collect_component_ref_target_ids_under_root(
  rootId: string,
  allTargets: TestTarget[]
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];

  function dfs(parentId: string): void {
    const children = allTargets.filter((t) => t.parentId === parentId);
    for (const child of children) {
      if (child.type === "component-ref") {
        const rid = (
          child.params as { referenceTargetId?: unknown } | undefined
        )?.referenceTargetId;
        if (typeof rid === "string" && rid.trim()) {
          const trimmed = rid.trim();
          if (!seen.has(trimmed)) {
            seen.add(trimmed);
            out.push(trimmed);
          }
        }
        // component-ref はリーフ — 子に降りない
      } else if (child.type === "component" || child.type === "view") {
        // 境界ノード — 降りない
      } else {
        // section / overlay / item / etc. — 再帰降下
        dfs(child.id);
      }
    }
  }

  dfs(rootId);
  return out;
}

/**
 * 指定 component から親方向へ辿り、ルートに近い順の component チェーンを返す。
 */
export function get_component_ancestor_chain(
  leaf_component_id: string,
  targets: TestTarget[]
): TestTarget[] {
  const chain: TestTarget[] = [];
  let current: TestTarget | undefined = targets.find(
    (t) => t.id === leaf_component_id
  );
  while (current) {
    if (current.type === "component") {
      chain.unshift(current);
    }
    const pid = current.parentId;
    if (!pid) {
      break;
    }
    current = targets.find((t) => t.id === pid);
  }
  return chain;
}

/** 指定ルート配下の item・section・overlay を DFS で取得 */
export type ViewItemPanelRow = TestTarget & {
  level?: number;
  sourceComponentId?: string;
  sourceComponentName?: string;
  componentChainDepth?: number;
};

export function build_item_hierarchy_under_root(
  rootId: string,
  allTargets: TestTarget[]
): ViewItemPanelRow[] {
  const build = (parentId: string, level: number): ViewItemPanelRow[] => {
    const directChildren = allTargets.filter(
      (t) =>
        t.parentId === parentId &&
        !is_feature_target(t) &&
        (t.type === "item" || t.type === "section" || t.type === "overlay")
    );
    let result: ViewItemPanelRow[] = [];
    directChildren.forEach((child) => {
      result.push({ ...child, level });
      result.push(...build(child.id, level + 1));
    });
    return result;
  };
  return build(rootId, 0);
}

export type ComponentTableDisplayRow =
  | {
      rowKind: "component_header";
      rowKey: string;
      name: string;
      depth: number;
    }
  | { rowKind: "item"; rowKey: string; panelRow: ViewItemPanelRow };

/**
 * View の component_ids に紐づく共通コンポーネント配下の項目を、表用の行リストに展開する。
 *
 * NOTE(#811): component-ref はツリー側でインライン表示するため、本一括表には含めない
 * （配置文脈ごと表示スライス）。
 */
export function build_component_table_display_rows(
  viewTarget: TestTarget,
  allTargets: TestTarget[]
): ComponentTableDisplayRow[] {
  const displayRows: ComponentTableDisplayRow[] = [];
  // 旧モデル（component_ids, View直下のみ）専用。新 component-ref はツリー側で
  // 配置位置に表示するため、本一括表には含めない（#811 配置文脈ごと表示スライス）。
  const rawIds = unique_trimmed_component_ids(
    get_component_ids_from_view_params(viewTarget.params)
  );
  for (const rawId of rawIds) {
    const comp = resolve_component_by_stored_id(rawId, allTargets);
    if (!comp) {
      continue;
    }
    const ancestor_chain = get_component_ancestor_chain(comp.id, allTargets);
    const chain_depth = ancestor_chain.length;
    for (let i = 0; i < ancestor_chain.length; i++) {
      const c = ancestor_chain[i];
      displayRows.push({
        rowKind: "component_header",
        rowKey: `comp-hdr-${comp.id}-${c.id}`,
        name: c.name,
        depth: i,
      });
    }
    const under = build_item_hierarchy_under_root(comp.id, allTargets);
    under.forEach((row) => {
      const panelRow: ViewItemPanelRow = {
        ...row,
        sourceComponentId: comp.id,
        sourceComponentName: comp.name,
        componentChainDepth: chain_depth,
      };
      displayRows.push({
        rowKind: "item",
        rowKey: `comp-item-${comp.id}-${row.id}`,
        panelRow,
      });
    });
  }
  return displayRows;
}

/**
 * View（または任意ルート）配下を DFS し、描画用に
 * item / section / overlay / component-ref を level 付き・ツリー順で返す。
 * component-ref はリーフ扱い（その子には降りない）。
 * 統計・選択用の items プロップとは別系統で、描画専用。
 */
export function build_view_render_rows(
  rootId: string,
  allTargets: TestTarget[]
): (TestTarget & { level: number })[] {
  const build = (
    parentId: string,
    level: number
  ): (TestTarget & { level: number })[] => {
    const directChildren = allTargets.filter(
      (t) =>
        t.parentId === parentId &&
        !is_feature_target(t) &&
        (t.type === "item" ||
          t.type === "section" ||
          t.type === "overlay" ||
          t.type === "component-ref")
    );
    let result: (TestTarget & { level: number })[] = [];
    for (const child of directChildren) {
      result.push({ ...child, level });
      if (child.type !== "component-ref") {
        result.push(...build(child.id, level + 1));
      }
    }
    return result;
  };
  return build(rootId, 0);
}

function get_all_descendant_targets(
  parent_id: string,
  targets: TestTarget[]
): TestTarget[] {
  const direct = targets.filter((t) => t.parentId === parent_id);
  let out: TestTarget[] = [];
  for (const c of direct) {
    out.push(c);
    out = out.concat(get_all_descendant_targets(c.id, targets));
  }
  return out;
}

/** 親 Service 配下に存在する component ターゲット（子孫すべて） */
export function get_components_under_service(
  service_id: string,
  targets: TestTarget[]
): TestTarget[] {
  return get_all_descendant_targets(service_id, targets).filter(
    (t) => t.type === "component"
  );
}

/** component-ref ノードの referenceTargetId から参照先 component を解決する。 */
export function resolve_component_ref_target(
  refNode: TestTarget,
  allTargets: TestTarget[]
): TestTarget | undefined {
  const rid = (
    refNode.params as { referenceTargetId?: unknown } | undefined
  )?.referenceTargetId;
  if (typeof rid !== "string" || !rid.trim()) {
    return undefined;
  }
  return resolve_component_by_stored_id(rid, allTargets);
}

/**
 * component-ref の参照先 component 配下の item/section/overlay を level 付きで返す。
 * 展開表示用（読み取り専用）。dangling／参照先が空なら空配列。
 */
export function build_component_ref_child_rows(
  refNode: TestTarget,
  allTargets: TestTarget[]
): ViewItemPanelRow[] {
  const comp = resolve_component_ref_target(refNode, allTargets);
  if (!comp) {
    return [];
  }
  return build_item_hierarchy_under_root(comp.id, allTargets);
}
